import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LocaleInitialState } from './types';
import { splitLocale } from './utils';
import { getUrlCurrentLocaleCodes } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';
import cookieReader from 'js-cookie';
import { decodeToken, isTokenExpired } from '../auth/utils';

/**
 * Resolve the initial locale for the intl store.
 *
 * 1. Try the URL path (/:country/:locale/…)
 * 2. If the URL didn't contain a valid locale prefix, check the user's
 *    JWT token in cookies — after a page reload on a path like
 *    /email-verified the URL has no locale prefix, but the cookie token
 *    still contains the user's country/language.
 */
const resolveInitialLocale = (): LocaleInitialState => {
  const urlCodes = getUrlCurrentLocaleCodes();

  // Check whether the URL actually had a valid locale prefix by looking at
  // the raw path segments.  getUrlCurrentLocaleCodes falls back to defaults
  // when the segments don't match the expected lengths.
  const segments = window.location.pathname.split('/');
  const hasLocaleInUrl = segments[1]?.length === 2 && segments[2]?.length === 5;

  if (hasLocaleInUrl) {
    return urlCodes;
  }

  // URL has no locale prefix — try the JWT token from cookies
  try {
    const tokenName = import.meta.env.VITE_SECURITY_TOKEN_NAME;
    const userToken = tokenName ? cookieReader.get(tokenName) || '' : '';
    if (userToken) {
      const decoded = decodeToken(userToken);
      if (!isTokenExpired(decoded) && decoded.country && decoded.language) {
        const locale = `${decoded.language}_${decoded.country}`;
        return {
          locale,
          language: decoded.language,
          country: decoded.country,
        };
      }
    }
  } catch {
    // token decode failed — fall through to URL defaults
  }

  return urlCodes;
};

const { country, language, locale } = resolveInitialLocale();

export const initialState: LocaleInitialState = {
  locale,
  language,
  country,
};

export const intlSlice = createSlice({
  name: 'intl@',
  initialState,
  reducers: {
    changeLocale(state, { payload }: PayloadAction<string>) {
      const { locale, language, country } = splitLocale(payload);

      state.locale = locale;
      state.language = language;
      state.country = country;
    },
  },
});

export default intlSlice;
